import { get } from './client'

// Marketing channel registry. The spend-entry UI builds its inputs from this
// rather than a hardcoded list, so adding a channel gives it an input and
// includes it in aggregation without a schema change (§9.6).
export type MarketingChannel = {
  id:          string   // matches the key used in AdSpendEntry.channels
  displayName: string
  platform:    string
  group:       'social' | 'search' | 'video' | 'messaging' | 'organic' | 'other'
}

function qs(shop: string, params: Record<string, string> = {}): string {
  const sp = new URLSearchParams({ shop, ...params })
  return `?${sp.toString()}`
}

// Subset of the Marketing Intelligence response the Control Center reads.
// MER is revenue ÷ spend blended across channels; `roas` stays null until
// per-channel revenue attribution exists, so MER is what can honestly be shown.
export type MarketingMetrics = {
  totalAdSpend:               number
  netRevenue:                 number
  grossProfit:                number
  mer:                        number
  roas:                       number | null
  contributionMarginAfterAds: number
  cmAfterAdsRate:             number
  newCustomers:               number
  cac:                        number | null
  avgOrderValue:              number
  confidence: {
    level: 'high' | 'medium' | 'low'
    score: number
  }
}

// Return on ad spend split by what actually happened to the orders. Ad platforms
// count an order the moment it is placed; in a COD market a fifth or more never
// complete, so booked ROAS is what was promised, not what was earned.
// All three are performance-lens: orders grouped by created_at, matched against
// the spend recorded for that same period.
export type RoasBreakdown = {
  adSpend:          number

  bookedRevenue:    number
  deliveredRevenue: number
  failedRevenue:    number
  openRevenue:      number

  bookedRoas:       number   // every order created in the period
  deliveredRoas:    number   // only what reached the customer
  projectedRoas:    number   // delivered + open revenue at this cohort's success rate

  resolvedSuccessRate: number   // 0–100, revenue-weighted
  revenueResolvedPct:  number   // 0–100 — how much of booked revenue has a known outcome
}

export type MarketingIntelligence = {
  month:         string
  metrics:       MarketingMetrics
  roasBreakdown: RoasBreakdown
}

export const marketingApi = {
  getChannels: (shop: string): Promise<{ channels: MarketingChannel[] }> =>
    get(`/marketing/channels${qs(shop)}`),

  getIntelligence: (shop: string, month?: string): Promise<MarketingIntelligence> => {
    const params: Record<string, string> = {}
    if (month) params['month'] = month
    return get(`/marketing/intelligence${qs(shop, params)}`)
  },
}
